import { Box, Grid } from '@mui/material'
import {
    fromFacilityCode,
    fromFrequencyCode,
    fromPOSCode,
    fromTypeOfCareCode,
    getDateFromString,
} from '../utils'
import { DataPanel } from './DataPanel'
import { DataTable } from './DataTable'

interface InstitutionalClaimDetailsProps {
    data: any
}

export const InstitutionalClaimDetails = ({ data }: InstitutionalClaimDetailsProps) => {
    let isClaimPrivateDataAvailable = false
    let dateOfAdmission: string | undefined
    let statementPeriodStart: string | undefined
    let statementPeriodEnd: string | undefined
    const occurrences = []
    const values = []
    if (data.claim !== undefined) {
        const privateData = data.claim?.privateData
        if (privateData !== undefined) {
            isClaimPrivateDataAvailable = true
            if (data.claim?.publicData.dateOfAdmission !== undefined) {
                dateOfAdmission = getDateFromString(
                    data.claim?.publicData.dateOfAdmission,
                ).toLocaleDateString()
            }
            const statementPeriodMatches = (data.claim?.publicData.statementPeriod ?? '').match(
                /^(\d{8})\s*-\s*(\d{8})$/,
            )
            if (statementPeriodMatches && statementPeriodMatches.length === 3) {
                statementPeriodStart = getDateFromString(
                    statementPeriodMatches[1],
                ).toLocaleDateString()
                statementPeriodEnd = getDateFromString(
                    statementPeriodMatches[2],
                ).toLocaleDateString()
            }

            if (data.claim && data.claim.privateData && data.claim.privateData.occurrences) {
                let first = true
                for (const occurrence of data.claim.privateData.occurrences) {
                    let key = ''
                    if (first) {
                        key = 'Occurrences Codes'
                        first = false
                    }
                    occurrences.push({
                        key,
                        value: `${occurrence.code} ${getDateFromString(
                            occurrence.date,
                        ).toLocaleDateString()}`,
                        private: true,
                    })
                }
            }
        }
        if (data.claim && data.claim.publicData.values) {
            let first = true
            for (const value of data.claim.publicData.values) {
                let key = ''
                if (first) {
                    key = 'Value Codes'
                    first = false
                }
                values.push({ key, value: `${value.code} ${value.price}`, private: false })
            }
        }
    } else {
        isClaimPrivateDataAvailable = true
    }

    const factilityTypeCode = (data.claim?.publicData.billType ?? '').charAt(0)
    const typeOfCareCode = (data.claim?.publicData.billType ?? '').charAt(0)
    const facilityType = `${fromFacilityCode(factilityTypeCode)} (${factilityTypeCode})`
    const typeOfCare = `${fromTypeOfCareCode(typeOfCareCode)} (${typeOfCareCode})`
    const placeOfService = `${fromPOSCode(data.claim?.privateData?.placeOfService)} (${
        data.claim?.privateData?.placeOfService
    })`
    const claimDetailsContent = (
        <>
            <DataTable
                isPrivateDataAvailable={isClaimPrivateDataAvailable}
                data={[
                    {
                        key: 'Patient Account Number',
                        value: data.claim?.publicData.claimNumber,
                        private: false,
                    },
                    { key: 'Date of Admission', value: dateOfAdmission, private: false },
                    {
                        key: 'Statement Period Start',
                        value: `${statementPeriodStart} - ${statementPeriodEnd}`,
                        private: false,
                    },
                    { key: 'Place of Service', value: placeOfService, private: true },
                ]}
            />
            <DataTable isPrivateDataAvailable={isClaimPrivateDataAvailable} data={occurrences} />
            <Box
                sx={{
                    marginTop: '20px',
                    marginBottom: '20px',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            />
            <DataTable isPrivateDataAvailable={isClaimPrivateDataAvailable} data={values} />
        </>
    )

    const claimsAdmissionSummary = (
        <>
            <DataTable
                isPrivateDataAvailable={isClaimPrivateDataAvailable}
                data={[
                    {
                        key: 'Principal Diagnosis Code',
                        value: data.claim?.publicData.principalDiagnosisCode,
                        private: false,
                    },
                    {
                        key: 'Secondary Diagnosis Codes',
                        value: data.claim?.publicData.secondaryDiagnosisCodes,
                        private: false,
                    },
                    {
                        key: 'Number of Covered Days',
                        value: data.claim?.publicData.numberOfCoveredDays,
                        private: false,
                    },
                    { key: 'Bill Type', value: `${facilityType} / ${typeOfCare}`, private: false },
                    {
                        key: 'Frequency Code',
                        value: fromFrequencyCode(data.claim?.privateData?.frequencyCode),
                        private: true,
                    },
                ]}
            />
            <Box
                sx={{
                    marginTop: '20px',
                    marginBottom: '20px',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            />
        </>
    )

    return (
        <Grid container spacing={4}>
            <Grid item lg={6} md={12} xs={12}>
                <DataPanel minHeight={150} title="Claim Details" content={claimDetailsContent} />
            </Grid>
            <Grid item container lg={6} md={12} xs={12} spacing={4}>
                <Grid item xs={12}>
                    <DataPanel minHeight={150} title="" content={claimsAdmissionSummary ?? <></>} />
                </Grid>
            </Grid>
        </Grid>
    )
}
